/**
 * A REGRA dos encargos patronais e do FGTS rescisório. Módulo PURO.
 *
 * O que o empregador paga ALÉM do salário, e o que o desligamento custa. Sem
 * import de rede, sem relógio, e sem valor que expire: as alíquotas abaixo são
 * as estruturais (Lei 8.212/91 e Lei 8.036/90), e as que variam por empresa
 * — RAT, FAP, terceiros, alíquota da CPRB — são ENTRADA, porque dependem do
 * CNAE e do FPAS de cada uma. Não há tabela a expirar aqui.
 */

import { ALIQUOTA_FGTS, centavos } from "./folhaDp";

/** INSS patronal no regime de folha: 20% (Lei 8.212/91 art. 22, I). */
export const INSS_PATRONAL = 0.2;

/** Faixas válidas do FAP (Decreto 6.042/2007): multiplica o RAT. */
export const FAP_MIN = 0.5;
export const FAP_MAX = 2.0;

/** Graus de risco do RAT: 1%, 2% ou 3% (Lei 8.212/91 art. 22, II). */
export const RAT_VALIDOS = [1, 2, 3] as const;

export type Regime = "folha" | "cprb";

export interface EntradaEncargos {
  /** Soma dos salários brutos da competência — base do INSS patronal e de tudo mais. */
  folha: number;
  /** Receita bruta do mês. Só usada no regime CPRB. */
  receitaBruta: number;
  regime: Regime;
  /** Alíquota da CPRB em PERCENTUAL (1,0 a 4,5) — varia por atividade. */
  cprbPercentual: number;
  /** Grau de risco: 1, 2 ou 3 (percentual). */
  ratPercentual: number;
  /** Fator Acidentário de Prevenção, 0,5 a 2,0. */
  fap: number;
  /** Soma das contribuições a terceiros em percentual — varia por FPAS. */
  terceirosPercentual: number;
  incluirFgts: boolean;
}

export interface ResultadoEncargos {
  regime: Regime;
  /** Base do INSS patronal: a folha no regime de folha, a receita na CPRB. */
  basePrevidenciaria: number;
  inssPatronal: number;
  /** RAT × FAP, o percentual efetivo. */
  ratEfetivoPercentual: number;
  rat: number;
  terceiros: number;
  fgts: number;
  total: number;
  /**
   * total ÷ folha, em percentual.
   *
   * É sempre sobre a FOLHA, mesmo na CPRB — a pergunta que o contador faz é
   * "quanto custa cada real de salário", e essa pergunta não muda de base
   * conforme o regime. Na CPRB o número pode ficar muito alto ou muito baixo,
   * porque o INSS ali não vem da folha: é informação, não defeito, e a tela diz.
   */
  custoSobreFolhaPercentual: number;
  /** True quando a folha é zero: o percentual não tem sentido e a tela omite. */
  semFolha: boolean;
}

/**
 * Os encargos do mês.
 *
 * **RAT/FAP e terceiros incidem sobre a FOLHA sempre, inclusive na CPRB.** É o
 * erro mais comum do módulo: a desoneração (Lei 12.546/2011) substitui apenas a
 * contribuição previdenciária patronal de 20% — não toca no RAT, nos terceiros
 * nem no FGTS. Quem calcula CPRB zerando tudo sobre a folha subestima o custo.
 */
export function calcularEncargos(e: EntradaEncargos, p?: unknown): ResultadoEncargos {
  void p; // assinatura uniforme com os outros módulos; aqui não há parâmetro do banco
  const folha = Math.max(0, e.folha);
  const receita = Math.max(0, e.receitaBruta);
  const naCprb = e.regime === "cprb";

  const basePrevidenciaria = naCprb ? receita : folha;
  const inssPatronal = naCprb
    ? centavos(receita * (Math.max(0, e.cprbPercentual) / 100))
    : centavos(folha * INSS_PATRONAL);

  // RAT × FAP, e as duas pontas do FAP são aparadas: fora de 0,5–2,0 não existe
  // no Decreto 6.042, e aceitar 10 daria um encargo dez vezes maior sem aviso.
  const fap = Math.min(FAP_MAX, Math.max(FAP_MIN, e.fap));
  const ratEfetivoPercentual = Math.round(Math.max(0, e.ratPercentual) * fap * 10000) / 10000;
  const rat = centavos(folha * (ratEfetivoPercentual / 100));
  const terceiros = centavos(folha * (Math.max(0, e.terceirosPercentual) / 100));
  const fgts = e.incluirFgts ? centavos(folha * ALIQUOTA_FGTS) : 0;

  const total = centavos(inssPatronal + rat + terceiros + fgts);

  return {
    regime: e.regime,
    basePrevidenciaria,
    inssPatronal,
    ratEfetivoPercentual,
    rat,
    terceiros,
    fgts,
    total,
    custoSobreFolhaPercentual: folha > 0 ? Math.round((total / folha) * 10000) / 100 : 0,
    semFolha: folha <= 0,
  };
}

// ---------------------------------------------------------------------------
// FGTS rescisório
// ---------------------------------------------------------------------------

export type MotivoRescisao =
  | "semJustaCausa"
  | "culpaReciproca"
  | "comJustaCausa"
  | "pedidoDemissao"
  | "acordoMutuo";

interface RegraRescisao {
  rotulo: string;
  /** Multa sobre o saldo, em percentual. */
  multaPercentual: number;
  /** Fração do saldo que o trabalhador pode sacar (0 = não saca). */
  fracaoSaque: number;
  /** Tem direito ao seguro-desemprego? */
  seguroDesemprego: boolean;
  /** Aviso prévio: quem paga e quanto. */
  avisoPrevio: string;
  base: string;
}

/**
 * As cinco saídas e o que cada uma custa.
 *
 * Tabela e não `switch` porque os cinco campos de cada motivo andam juntos — a
 * multa, o saque, o seguro e o aviso. Espalhados em ramos, é assim que um passa
 * a discordar do outro (o "nome do tema em dois lugares" do Gerador de
 * Currículos).
 */
export const RESCISAO: Record<MotivoRescisao, RegraRescisao> = {
  semJustaCausa: {
    rotulo: "Dispensa sem justa causa",
    multaPercentual: 40,
    fracaoSaque: 1,
    seguroDesemprego: true,
    avisoPrevio: "Pago pelo empregador (30 dias + 3 por ano, até 90)",
    base: "CLT art. 477 e Lei 8.036/90 art. 18",
  },
  culpaReciproca: {
    rotulo: "Culpa recíproca",
    multaPercentual: 20,
    fracaoSaque: 1,
    seguroDesemprego: false,
    avisoPrevio: "Metade, por ambas as partes",
    base: "CLT art. 484 e Súmula 14 do TST",
  },
  comJustaCausa: {
    rotulo: "Dispensa por justa causa",
    multaPercentual: 0,
    fracaoSaque: 0,
    seguroDesemprego: false,
    avisoPrevio: "Não há",
    base: "CLT art. 482",
  },
  pedidoDemissao: {
    rotulo: "Pedido de demissão",
    multaPercentual: 0,
    fracaoSaque: 0,
    seguroDesemprego: false,
    avisoPrevio: "Devido pelo empregado (ou descontado)",
    base: "CLT art. 487, §2º",
  },
  acordoMutuo: {
    rotulo: "Acordo entre as partes",
    multaPercentual: 20,
    // O acordo do art. 484-A saca 80% do saldo — o resto fica na conta
    // vinculada. É a única das cinco em que o saque é parcial.
    fracaoSaque: 0.8,
    seguroDesemprego: false,
    avisoPrevio: "Metade, se indenizado",
    base: "CLT art. 484-A (Reforma de 2017)",
  },
};

export interface ResultadoRescisao {
  motivo: MotivoRescisao;
  regra: RegraRescisao;
  saldo: number;
  multa: number;
  saqueDisponivel: number;
  /** O que fica retido na conta vinculada (só o acordo 484-A tem isto). */
  permaneceNaConta: number;
  /** Multa + o que o empregador deposita: o custo do desligamento no FGTS. */
  custoEmpregador: number;
}

/**
 * O FGTS na rescisão.
 *
 * A multa é sobre o SALDO da conta vinculada — todo o depositado ao longo do
 * contrato, não o salário nem o último depósito. É o campo que o aluno mais
 * confunde, e a tela pede o saldo com essa palavra.
 */
export function calcularRescisao(saldo: number, motivo: MotivoRescisao): ResultadoRescisao {
  const regra = RESCISAO[motivo];
  const s = Math.max(0, saldo);
  const multa = centavos(s * (regra.multaPercentual / 100));
  const saqueDisponivel = centavos(s * regra.fracaoSaque);
  return {
    motivo,
    regra,
    saldo: s,
    multa,
    saqueDisponivel,
    permaneceNaConta: centavos(s - saqueDisponivel),
    // A multa é depositada na conta do trabalhador e sacada junto — para o
    // empregador é custo do desligamento, e é este o número que ele precisa
    // provisionar.
    custoEmpregador: multa,
  };
}
